import {
  thunk,
  action,
  computed,
  type Computed,
  type Action,
  type Thunk,
} from "easy-peasy";
import moment from "moment";
import { Cookies } from "react-cookie";
import {
  AUTH_TOKEN,
  ENV_MAPPER,
  TOKEN_TTL,
  REFRESH_TOKEN,
  SELECTED_ORGANIZATION,
} from "utils/constants";
import {
  decodeReadStringifyObj,
  emptyProxyObject,
  getEnv,
  getSessionStorage,
  type ProxyObjectType,
} from "utils/utils";
import { mutate } from "utils/relay";
import { setAuthRefreshHandler } from "api/client";

const cookie = new Cookies();

// Legacy refresh mutation used by older flows
// const mutation = graphql`
//   mutation authRefreshMutation($input: RefreshInput!) {
//     refreshToken(input: $input) {
//       token
//       payload
//       refreshToken
//     }
//   }
// `;

export interface CurrentUserModel {
  username: string;
  email: string;
}

export interface TokenPayload {
  self: string;
  expiry: Date;
}

export interface SelectedOrganization {
  group: string;
  group_name: string;
  is_direct_member: boolean;
  roles: string[];
  permissions: string[];
}

export interface AuthModel {
  currentUser: CurrentUserModel | ProxyObjectType;
  authToken: string | null;
  refreshToken: string | null;
  permissions: any;
  selectedOrganization: SelectedOrganization | null;

  isLoggedIn: Computed<AuthModel, boolean>;

  setAuthToken: Action<AuthModel, string | null>;
  setRefreshToken: Action<AuthModel, string | null>;
  setCurrentUser: Action<AuthModel, CurrentUserModel | ProxyObjectType>;
  setPermissions: Action<AuthModel, any>;
  setSelectedOrganization: Action<AuthModel, SelectedOrganization | null>;

  updateAuthToken: Thunk<AuthModel, TokenPayload>;
  updateRefreshToken: Thunk<AuthModel, TokenPayload>;
  clearAuthToken: Thunk<AuthModel>;
  clearRefreshToken: Thunk<AuthModel>;
  updatePermissions: Thunk<AuthModel, string[] | string | undefined>;
  fetchAuthToken: Thunk<AuthModel, any>;
  initRefreshToken: Thunk<AuthModel, boolean | undefined>;
}

let refreshIntervalId: number | null = null;

const AuthStore: AuthModel = {
  currentUser: emptyProxyObject,
  authToken: getSessionStorage(AUTH_TOKEN) ?? null,
  refreshToken: cookie.get(REFRESH_TOKEN) ?? null,
  permissions: {},
  selectedOrganization: decodeReadStringifyObj(SELECTED_ORGANIZATION) ?? null,

  isLoggedIn: computed((state) => Boolean(state.authToken)),

  setAuthToken: action((state, authToken) => {
    state.authToken = authToken;
  }),

  setRefreshToken: action((state, refreshToken) => {
    state.refreshToken = refreshToken;
  }),

  setCurrentUser: action((state: any, currentUser) => {
    if (!state) {
      return;
    }
    state.currentUser = currentUser ?? emptyProxyObject;
  }),

  setPermissions: action((state, permissions) => {
    state.permissions = permissions;
  }),

  setSelectedOrganization: action((state, organization) => {
    state.selectedOrganization = organization;
  }),

  updateAuthToken: thunk((actions, token) => {
    window.sessionStorage.setItem(AUTH_TOKEN, JSON.stringify(token.self));
    actions.setAuthToken(token.self);
  }),

  updateRefreshToken: thunk((actions, token) => {
    cookie.set(REFRESH_TOKEN, token.self, { path: "/" });
    actions.setRefreshToken(token.self);
  }),

  clearAuthToken: thunk((actions) => {
    window.localStorage.removeItem(AUTH_TOKEN);
    window.sessionStorage.removeItem(AUTH_TOKEN);
    actions.setAuthToken(null);
    actions.setCurrentUser(emptyProxyObject);
    actions.setPermissions({});
    actions.setSelectedOrganization(null);
  }),

  clearRefreshToken: thunk((actions) => {
    cookie.remove(REFRESH_TOKEN, { path: "/" });
    actions.setRefreshToken(null);
  }),

  updatePermissions: thunk((actions, permissions) => {
    const perms: Record<string, boolean> = {};
    const permissionList = Array.isArray(permissions)
      ? permissions
      : permissions
        ? [permissions]
        : [];
    permissionList.forEach((p: string) => {
      perms[p] = true;
    });
    actions.setPermissions(perms);
  }),

  fetchAuthToken: thunk(
    (actions, { mutation, input, onSuccess, onFailure }) => {
      mutate({
        mutation,
        input,
        onSuccess: async (data: any) => {
          const unixTime = Number(
            moment.unix(data.login?.refreshExpiresIn || 0)
          );
          const expiry = new Date(unixTime);
          if (data.login) {
            // authToken should only be set via idpRefresh
            // await actions.updateAuthToken({ self: data.login.token, expiry });
            await actions.updateRefreshToken({
              self: data.login.refreshToken,
              expiry,
            });
          }
          onSuccess(data);
        },
        onFailure: (messages: any[]) => {
          actions.clearAuthToken();
          actions.clearRefreshToken();
          onFailure(messages);
        },
      });
    }
  ),

  initRefreshToken: thunk(() => {
    if (refreshIntervalId) clearInterval(refreshIntervalId);
    setAuthRefreshHandler(null);
    return Promise.resolve(false);
  }),
};

export default AuthStore;
